import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRightOutlined,
  DeleteOutlined,
  SaveOutlined,
  ThunderboltOutlined,
  PlusOutlined,
  RiseOutlined,
  CheckCircleOutlined,
  MessageOutlined,
  HistoryOutlined,
  ClockCircleOutlined,
  UserOutlined,
  FileOutlined,
  CommentOutlined,
  EditOutlined,
  CloseCircleOutlined,
  UploadOutlined,
  QuestionCircleOutlined,
  CheckOutlined,
  StopOutlined,
  PaperClipOutlined,
  DownloadOutlined,
  AppstoreOutlined,
  CalendarOutlined,
  FilterOutlined,
  UpOutlined,
  DownOutlined,
  OrderedListOutlined,
  FileTextOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Checkbox,
  Col,
  Empty,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Slider,
  Space,
  Spin,
  Upload,
  Tabs,
  Tag,
  Typography,
  Radio,
  Tooltip,
} from 'antd';
import MainLayout from '../../layouts/MainLayout';
import PersianDatePicker from '../../components/PersianDatePicker';
import { numberFormatter, numberParser } from '../../utils/numberUtils';
import { toGregorian, toJalali, isValidDate, isAfter } from '../../utils/dateUtils';
import moment from 'moment-jalaali';
import { actionApi, ActionValidationError } from '../../utils/actionApi';
import { getSuccessMessage } from '../../utils/actionMessageTranslator';
import ActionRaciTab from './components/ActionRaciTab';
import ActionProgressTab from './components/ActionProgressTab';
import ActionAcceptanceTab from './components/ActionAcceptanceTab';
import ActionCommentsTab from './components/ActionCommentsTab';
import ActionRequestsTab from './components/ActionRequestsTab';
import ActionAttachmentsTab from './components/ActionAttachmentsTab';
import ActionResourcesTab from './components/ActionResourcesTab';
import ActionItemsTab from './components/ActionItemsTab';
import { DetailPageSkeleton, CommentsSkeleton } from '../../components/SkeletonLoaders';

const { Title, Text } = Typography;
const { TextArea } = Input;

const tabPrimaryBtnClass =
  '!bg-emerald-500 hover:!bg-emerald-600 !border-emerald-500 hover:!border-emerald-600 !text-white';

const enumOptions = {
  priorities: [
    { value: 'فوری_و_اضطراری', label: 'فوری و اضطراری', color: 'red' },
    { value: 'بسیار_مهم', label: 'بسیار مهم', color: 'volcano' },
    { value: 'مهم', label: 'مهم', color: 'orange' },
    { value: 'کم_اهمیت', label: 'کم اهمیت', color: 'green' },
    { value: 'غیرضروری', label: 'غیرضروری', color: 'default' },
  ],
  actionTypes: [
    { value: 'اصلاح', label: 'اصلاح' },
    { value: 'اقدام_پیشگیرانه', label: 'اقدام پیشگیرانه' },
    { value: 'اقدام_اصلاحی', label: 'اقدام اصلاحی' },
  ],
  statuses: [
    { value: 'باز', label: 'باز', color: 'blue' },
    { value: 'در_حال_برنامه_ریزی', label: 'در حال برنامه‌ریزی', color: 'orange' },
    { value: 'در_حال_انجام', label: 'در حال انجام', color: 'cyan' },
    { value: 'بسته', label: 'بسته', color: 'default' },
  ],
};

// Event types for timeline (فقط برای UI)
const TimelineEventTypes = {
  ایجاد: { label: 'ایجاد', color: 'blue', icon: <PlusOutlined /> },
  پذیرش: { label: 'پذیرش', color: 'green', icon: <CheckCircleOutlined /> },
  بروزرسانی_پیشرفت: { label: 'به‌روزرسانی پیشرفت', color: 'cyan', icon: <RiseOutlined /> },
  درخواست_بستن: { label: 'درخواست بستن', color: 'orange', icon: <ClockCircleOutlined /> },
  بستن: { label: 'بستن', color: 'red', icon: <CloseCircleOutlined /> },
  افزودن_اقدام_فرعی: { label: 'افزودن اقدام فرعی', color: 'blue', icon: <PlusOutlined /> },
  ویرایش_اقدام_فرعی: { label: 'ویرایش اقدام فرعی', color: 'cyan', icon: <EditOutlined /> },
  حذف_اقدام_فرعی: { label: 'حذف اقدام فرعی', color: 'red', icon: <DeleteOutlined /> },
  تغییر_پیشرفت_اقدام_فرعی: { label: 'تغییر پیشرفت اقدام فرعی', color: 'cyan', icon: <RiseOutlined /> },
  افزودن_RACI: { label: 'افزودن RACI', color: 'blue', icon: <UserOutlined /> },
  ویرایش_RACI: { label: 'ویرایش RACI', color: 'cyan', icon: <EditOutlined /> },
  حذف_RACI: { label: 'حذف RACI', color: 'red', icon: <DeleteOutlined /> },
  آپلود_فایل: { label: 'آپلود فایل', color: 'blue', icon: <UploadOutlined /> },
  حذف_فایل: { label: 'حذف فایل', color: 'red', icon: <FileOutlined /> },
  تغییر_تاریخ_شروع_واقعی: { label: 'تغییر تاریخ شروع واقعی', color: 'orange', icon: <CalendarOutlined /> },
  تغییر_تاریخ_پایان_واقعی: { label: 'تغییر تاریخ پایان واقعی', color: 'orange', icon: <CalendarOutlined /> },
  کامنت: { label: 'نظر', color: 'default', icon: <CommentOutlined /> },
  درخواست_تمدید_ددلاین: { label: 'درخواست تمدید ددلاین', color: 'orange', icon: <ClockCircleOutlined /> },
  درخواست_منابع_بیشتر: { label: 'درخواست منابع بیشتر', color: 'gold', icon: <QuestionCircleOutlined /> },
  درخواست_انتقال_مسئولیت: { label: 'درخواست انتقال مسئولیت', color: 'geekblue', icon: <UserOutlined /> },
  درخواست_کمک: { label: 'درخواست کمک', color: 'cyan', icon: <QuestionCircleOutlined /> },
  تایید_درخواست: { label: 'تأیید درخواست', color: 'green', icon: <CheckOutlined /> },
  رد_درخواست: { label: 'رد درخواست', color: 'red', icon: <StopOutlined /> },
};

// گزینه‌های event_type برای فیلتر
const TimelineEventTypeOptions = Object.keys(TimelineEventTypes).map((key) => ({
  value: key,
  label: TimelineEventTypes[key].label,
}));

// استفاده از utility مرکزی برای تبدیل تاریخ
function toJalaliString(dateStr) {
  return toJalali(dateStr, 'jYYYY/jMM/jDD');
}

function formatDate(val) {
  if (!val) return null;
  
  // اگر DateObject از react-multi-date-picker باشد (که شامل ساعت است)
  if (val?.toDate && typeof val.toDate === 'function') {
    const dateObj = val.toDate();
    const m = moment(dateObj);
    if (m && m.isValid()) {
      // بررسی اینکه آیا ساعت هم دارد یا نه
      const hasTime = val.hour !== undefined || val.minute !== undefined || 
                      (dateObj.getHours() !== 0 || dateObj.getMinutes() !== 0 || dateObj.getSeconds() !== 0);
      if (hasTime) {
        // تبدیل به فرمت میلادی با ساعت برای API: "2025-12-28 14:00:00"
        return m.format('YYYY-MM-DD HH:mm:ss');
      }
      // بدون ساعت: فقط تاریخ میلادی
      return m.format('YYYY-MM-DD');
    }
  }
  
  // اگر رشته شامل ساعت است (مثلاً "1403/10/15 14:30" یا "1403/10/15 | 14:00")
  // این فرمت از PersianDatePicker می‌آید یا از API (با |)
  if (typeof val === 'string') {
    // جدا کردن تاریخ و ساعت
    let datePart, timePart;
    
    if (val.includes(' | ')) {
      // فرمت API: "1404/10/07 | 14:00"
      const parts = val.split(' | ');
      datePart = parts[0];
      timePart = parts[1] || '';
    } else if (val.includes(' ')) {
      // فرمت PersianDatePicker: "1403/10/15 14:30"
      const parts = val.split(' ');
      datePart = parts[0];
      timePart = parts[1] || '';
    } else {
      // فقط تاریخ بدون ساعت
      datePart = val;
      timePart = '';
    }
    
    // تبدیل تاریخ جلالی به میلادی
    const gregorianDate = toGregorian(datePart, 'YYYY-MM-DD');
    if (!gregorianDate) return null;
    
    if (timePart) {
      // اگر timePart فقط ساعت و دقیقه است (HH:mm)، ثانیه را اضافه می‌کنیم
      const timeParts = timePart.split(':');
      if (timeParts.length === 2) {
        return `${gregorianDate} ${timePart}:00`;
      }
      // اگر timePart شامل ثانیه است (HH:mm:ss)
      return `${gregorianDate} ${timePart}`;
    }
    
    // فقط تاریخ بدون ساعت
    return gregorianDate;
  }
  
  // برای سایر موارد، از toGregorian استفاده می‌کنیم
  return toGregorian(val, 'YYYY-MM-DD');
}

export default function EditAction() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [initial, setInitial] = useState(null);
  const [actionData, setActionData] = useState(null); // داده‌های کامل اقدام از API
  const [percent, setPercent] = useState(0);

  // پذیرش / بستن / پیشرفت
  const [acceptComment, setAcceptComment] = useState('');
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [progressPercent, setProgressPercent] = useState(0);
  const [progressComment, setProgressComment] = useState('');
  const [progressLoading, setProgressLoading] = useState(false);
  const [requestCloseComment, setRequestCloseComment] = useState('');
  const [requestCloseLoading, setRequestCloseLoading] = useState(false);
  const [closeComment, setCloseComment] = useState('');
  const [closeLoading, setCloseLoading] = useState(false);

  // نقش‌های من در این اقدام (پاسخگو، مسئول_اجرا و ...)
  const [myRoles, setMyRoles] = useState([]);
  
  // وضعیت‌های اقدام
  const [isAccepted, setIsAccepted] = useState(false);
  const [hasCloseRequest, setHasCloseRequest] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  
  // تعداد آیتم‌ها برای badge تب‌ها
  const [itemsCount, setItemsCount] = useState(0);
  const [resourcesCount, setResourcesCount] = useState(0);
  const [racisCount, setRacisCount] = useState(0);
  const [attachmentsCount, setAttachmentsCount] = useState(0);
  
  // Modal درخواست بستن
  const [requestCloseModalOpen, setRequestCloseModalOpen] = useState(false);

  // تایم‌لاین
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineFiltersOpen, setTimelineFiltersOpen] = useState(false);
  const [timelineFilters, setTimelineFilters] = useState({
    event_type: [],
    user_id: '',
    date_from: '',
    date_to: '',
  });

  // درخواست‌ها
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestType, setRequestType] = useState(undefined);
  const [requestTitle, setRequestTitle] = useState('');
  const [requestDescription, setRequestDescription] = useState('');
  const [requestRequestedTo, setRequestRequestedTo] = useState('');
  const [requestActionItemId, setRequestActionItemId] = useState('');
  const [requestDeadline, setRequestDeadline] = useState(null);
  const [requestNewUserId, setRequestNewUserId] = useState('');
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestedToActionId, setRequestedToActionId] = useState(null);
  const [newUserActionId, setNewUserActionId] = useState(null);

  // پیوست‌ها
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentContext, setAttachmentContext] = useState('default');
  const [attachmentDescription, setAttachmentDescription] = useState('');
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [attachmentFileList, setAttachmentFileList] = useState([]);
  const [editingAttachmentId, setEditingAttachmentId] = useState(null);
  const [editingAttachmentDescription, setEditingAttachmentDescription] = useState('');
  const [editingAttachmentLoading, setEditingAttachmentLoading] = useState(false);

  // منابع
  const [resources, setResources] = useState([]);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [resourcesSummary, setResourcesSummary] = useState(null);

  const fieldTranslations = useMemo(
    () => ({
      priority: 'اولویت',
      action_type: 'نوع اقدام',
      status: 'وضعیت',
      title: 'عنوان',
      description: 'توضیحات',
      objective: 'هدف',
      percent_complete: 'درصد پیشرفت',
      weight: 'وزن',
      planned_start_date: 'تاریخ شروع برنامه‌ریزی‌شده',
      planned_end_date: 'تاریخ پایان برنامه‌ریزی‌شده',
      actual_start_date: 'تاریخ شروع واقعی',
      actual_end_date: 'تاریخ پایان واقعی',
      source_module: 'ماژول منبع',
      source_id: 'شناسه منبع',
      has_breakdown: 'خردکاری',
      extra_description: 'توضیحات اضافی',
    }),
    []
  );

  const loadRequests = async () => {
    if (!id) return;
    setRequestsLoading(true);
    try {
      const res = await actionApi.getActionRequests(id, {});
      const list = res?.data || res?.data?.data || [];
      setRequests(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      message.error('خطا در دریافت درخواست‌ها');
    } finally {
      setRequestsLoading(false);
    }
  };

  const loadTimeline = async () => {
    if (!id) return;
    setTimelineLoading(true);
    try {
      const params = {};
      
      // آماده‌سازی فیلترها
      if (timelineFilters.event_type && timelineFilters.event_type.length > 0) {
        params.event_type = timelineFilters.event_type;
      }
      if (timelineFilters.user_id) {
        params.user_id = parseInt(timelineFilters.user_id);
      }
      if (timelineFilters.date_from) {
        // برای فیلترها، فقط تاریخ را به میلادی تبدیل می‌کنیم (بدون ساعت)
        const dateStr = typeof timelineFilters.date_from === 'string' 
          ? timelineFilters.date_from.split(' ')[0] // فقط قسمت تاریخ
          : timelineFilters.date_from;
        const gregorian = toGregorian(dateStr, 'YYYY-MM-DD');
        if (gregorian) params.date_from = gregorian;
      }
      if (timelineFilters.date_to) {
        // برای فیلترها، فقط تاریخ را به میلادی تبدیل می‌کنیم (بدون ساعت)
        const dateStr = typeof timelineFilters.date_to === 'string' 
          ? timelineFilters.date_to.split(' ')[0] // فقط قسمت تاریخ
          : timelineFilters.date_to;
        const gregorian = toGregorian(dateStr, 'YYYY-MM-DD');
        if (gregorian) params.date_to = gregorian;
      }

      const res = await actionApi.getActionTimeline(id, params);
      const list = res?.data || [];
      setTimeline(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      message.error('خطا در دریافت تایم‌لاین');
    } finally {
      setTimelineLoading(false);
    }
  };

  const loadAttachments = async () => {
    if (!id) return;
    setAttachmentsLoading(true);
    try {
      const res = await actionApi.getActionAttachments(id, { context: attachmentContext || undefined });
      const list = res?.data || res?.data?.data || [];
      setAttachments(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      message.error('خطا در دریافت پیوست‌ها');
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const load = async () => {
    setInitialLoading(true);
    try {
      const res = await actionApi.getActionById(id);
      const data = res?.data || res?.data?.data || res?.data?.action || res?.data?.data?.action;
      const action = data || res?.data;
      if (!res?.success) {
        message.error(res?.message || 'خطا در دریافت اطلاعات اقدام');
      }

      // تبدیل فرمت تاریخ از API به فرمت PersianDatePicker
      // از API: "1404/10/07 | 14:00" → برای PersianDatePicker: "1404/10/07 14:00"
      const convertApiDateToPicker = (dateStr) => {
        if (!dateStr) return null;
        // جایگزین " | " با فاصله برای PersianDatePicker
        return dateStr.replace(/\s*\|\s*/g, ' ');
      };

      const normalized = {
        title: action?.title ?? '',
        description: action?.description ?? '',
        objective: action?.objective ?? '',
        priority: action?.priority ?? undefined,
        action_type: action?.action_type ?? undefined,
        status: action?.status ?? undefined,
        percent_complete:
          typeof action?.percent_complete === 'number' ? action.percent_complete : Number(action?.percent_complete ?? 0),
        planned_start_date: convertApiDateToPicker(action?.planned_start_date),
        planned_end_date: convertApiDateToPicker(action?.planned_end_date),
        extra_description: action?.extra_description ?? '',
        weight: action?.weight ?? '',
        source_module: action?.source_module ?? '',
        source_id: action?.source_id ?? '',
      };

      // ذخیره نقش‌های کاربر در این اقدام (برای نمایش دکمه‌های پذیرش / بستن)
      setMyRoles(Array.isArray(action?.my_role) ? action.my_role : []);
      
      // ذخیره وضعیت‌های اقدام
      setIsAccepted(action?.is_accepted === true);
      setHasCloseRequest(action?.has_close_request === true);
      setCanDelete(action?.can_delete !== false);

      // ذخیره تعداد آیتم‌ها برای badge تب‌ها
      setItemsCount(action?.items_count || 0);
      setResourcesCount(action?.resources_count || 0);
      setRacisCount(action?.racis_count || 0);
      setAttachmentsCount(action?.attachments_count || 0);

      setInitial(normalized);
      setActionData(action); // ذخیره داده‌های کامل اقدام
      setPercent(Number(normalized.percent_complete || 0));
      setProgressPercent(Number(normalized.percent_complete || 0));
      form.setFieldsValue(normalized);
      // API های تب‌ها فقط وقتی روی تب کلیک می‌شود فراخوانی می‌شوند
    } catch (e) {
      console.error(e);
      message.error('خطا در دریافت اطلاعات اقدام');
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    if (id) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const buildPatchPayload = (values) => {
    if (!initial) return {};
    const current = {
      ...values,
      planned_start_date: formatDate(values.planned_start_date),
      planned_end_date: formatDate(values.planned_end_date),
    };

    Object.keys(current).forEach((k) => {
      if (current[k] === '' || current[k] === null || current[k] === undefined) delete current[k];
    });

    // حذف فیلدهای حذف شده از فرم
    delete current.percent_complete;
    delete current.extra_description;
    delete current.weight;

    if (current.source_id !== undefined) current.source_id = Number(current.source_id);

    // برای مقایسه، باید initial را هم به فرمت میلادی تبدیل کنیم
    const formatDateForComparison = (val) => {
      if (!val) return null;
      
      // اگر رشته شامل " | " است (فرمت API: "1404/10/07 | 14:00")
      if (typeof val === 'string' && val.includes(' | ')) {
        const parts = val.split(' | ');
        const datePart = parts[0];
        const timePart = parts[1] || '';
        const gregorianDate = toGregorian(datePart, 'YYYY-MM-DD');
        if (gregorianDate && timePart) {
          const timeParts = timePart.split(':');
          if (timeParts.length === 2) {
            return `${gregorianDate} ${timePart}:00`;
          }
          return `${gregorianDate} ${timePart}`;
        }
        return gregorianDate;
      }
      
      // اگر رشته شامل فاصله است (فرمت PersianDatePicker: "1404/10/07 14:00")
      if (typeof val === 'string' && val.includes(' ')) {
        const parts = val.split(' ');
        const datePart = parts[0];
        const timePart = parts[1] || '';
        const gregorianDate = toGregorian(datePart, 'YYYY-MM-DD');
        if (gregorianDate && timePart) {
          const timeParts = timePart.split(':');
          if (timeParts.length === 2) {
            return `${gregorianDate} ${timePart}:00`;
          }
          return `${gregorianDate} ${timePart}`;
        }
        return gregorianDate;
      }
      
      // فقط تاریخ
      return toGregorian(val, 'YYYY-MM-DD');
    };

    const initialComparable = {
      ...initial,
      planned_start_date: formatDateForComparison(initial.planned_start_date),
      planned_end_date: formatDateForComparison(initial.planned_end_date),
      source_id: initial.source_id !== '' ? Number(initial.source_id) : undefined,
    };

    const patch = {};
    Object.keys(current).forEach((k) => {
      if (String(current[k]) !== String(initialComparable[k])) patch[k] = current[k];
    });
    return patch;
  };

  const onSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = buildPatchPayload(values);
      if (Object.keys(payload).length === 0) {
        message.info('تغییری برای ذخیره وجود ندارد');
        return;
      }

      const res = await actionApi.updateAction(id, payload);
      if (res?.success) {
        message.success(getSuccessMessage(res.message, 'اقدام با موفقیت به‌روزرسانی شد'));
        await load();
      } else {
        message.error(res?.message || 'خطا در به‌روزرسانی اقدام');
      }
    } catch (error) {
      console.error(error);
      if (error instanceof ActionValidationError) {
        const fieldsError = Object.keys(error.errors || {}).map((fieldName) => {
          const errs = Array.isArray(error.errors[fieldName]) ? error.errors[fieldName] : [error.errors[fieldName]];
          const label = fieldTranslations[fieldName] || fieldName;
          return { name: fieldName, errors: errs.map((e) => `${label}: ${e}`) };
        });
        form.setFields(fieldsError);
        message.error(error.message || 'لطفاً خطاهای فرم را برطرف کنید');
      } else {
        message.error(error.message || 'خطا در به‌روزرسانی اقدام');
      }
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async () => {
    try {
      const res = await actionApi.deleteAction(id);
      if (res?.success) {
        message.success(getSuccessMessage(res.message, 'اقدام حذف شد'));
        navigate('/action/my-actions');
      } else {
        message.error(res?.message || 'خطا در حذف اقدام');
      }
    } catch (e) {
      console.error(e);
      message.error(e.message || 'خطا در حذف اقدام');
    }
  };

  const onAccept = async () => {
    setAcceptLoading(true);
    try {
      const comment = (acceptComment || '').trim();
      if (comment.length > 1000) {
        message.error('متن پذیرش حداکثر ۱۰۰۰ کاراکتر است');
        return;
      }

      const res = await actionApi.acceptAction(id, comment);
      if (res?.success) {
        message.success(getSuccessMessage(res.message, 'اقدام پذیرفته شد'));
        setAcceptComment('');
        await load();
      } else {
        message.error(res?.message || 'خطا در پذیرش اقدام');
      }
    } catch (error) {
      console.error(error);
      if (error instanceof ActionValidationError) {
        const commentErr = error.errors?.comment?.[0];
        message.error(commentErr || error.message || 'خطا در اعتبارسنجی پذیرش');
      } else {
        message.error(error.message || 'خطا در پذیرش اقدام');
      }
    } finally {
      setAcceptLoading(false);
    }
  };

  const onUpdateProgress = async () => {
    setProgressLoading(true);
    try {
      const pct = Number(progressPercent);
      if (Number.isNaN(pct) || pct < 0 || pct > 100) {
        message.error('درصد پیشرفت باید بین ۰ تا ۱۰۰ باشد');
        return;
      }
      const comment = (progressComment || '').trim();
      if (comment.length > 1000) {
        message.error('توضیحات حداکثر ۱۰۰۰ کاراکتر است');
        return;
      }

      const res = await actionApi.updateActionProgress(id, pct, comment);
      if (res?.success) {
        message.success(getSuccessMessage(res.message, 'پیشرفت اقدام به‌روزرسانی شد'));
        setProgressComment('');
        setPercent(pct);
        form.setFieldsValue({ percent_complete: pct });
        await load();
      } else {
        message.error(res?.message || 'خطا در به‌روزرسانی پیشرفت');
      }
    } catch (error) {
      console.error(error);
      if (error instanceof ActionValidationError) {
        const pctErr = error.errors?.percent_complete?.[0];
        const commentErr = error.errors?.comment?.[0];
        message.error(pctErr || commentErr || error.message || 'خطا در اعتبارسنجی پیشرفت');
      } else {
        message.error(error.message || 'خطا در به‌روزرسانی پیشرفت');
      }
    } finally {
      setProgressLoading(false);
    }
  };

  const handleRequestCloseClick = () => {
    setRequestCloseModalOpen(true);
  };

  const onRequestClose = async () => {
    setRequestCloseLoading(true);
    try {
      // اگر کاربر توضیحی وارد نکرده باشد، از متن پیش‌فرض استفاده می‌کنیم
      const comment = (requestCloseComment || '').trim();
      if (comment.length > 1000) {
        message.error('توضیحات درخواست بستن حداکثر ۱۰۰۰ کاراکتر است');
        setRequestCloseLoading(false);
        return;
      }

      const res = await actionApi.requestCloseAction(id, comment);
      if (res?.success) {
        message.success(getSuccessMessage(res.message, 'درخواست بستن ارسال شد'));
        setRequestCloseComment('');
        setRequestCloseModalOpen(false);
        await load();
      } else {
        message.error(res?.message || 'خطا در ارسال درخواست بستن');
      }
    } catch (error) {
      console.error(error);
      if (error instanceof ActionValidationError) {
        const commentErr = error.errors?.comment?.[0];
        message.error(commentErr || error.message || 'خطا در اعتبارسنجی درخواست بستن');
      } else {
        message.error(error.message || 'خطا در ارسال درخواست بستن');
      }
    } finally {
      setRequestCloseLoading(false);
    }
  };

  const onClose = async () => {
    setCloseLoading(true);
    try {
      const comment = (closeComment || '').trim();
      if (comment.length > 1000) {
        message.error('توضیحات بستن حداکثر ۱۰۰۰ کاراکتر است');
        return;
      }

      const res = await actionApi.closeAction(id, comment);
      if (res?.success) {
        message.success(getSuccessMessage(res.message, 'اقدام بسته شد'));
        setCloseComment('');
        await load();
      } else {
        message.error(res?.message || 'خطا در بستن اقدام');
      }
    } catch (error) {
      console.error(error);
      if (error instanceof ActionValidationError) {
        const commentErr = error.errors?.comment?.[0];
        message.error(commentErr || error.message || 'خطا در اعتبارسنجی بستن');
      } else {
        message.error(error.message || 'خطا در بستن اقدام');
      }
    } finally {
      setCloseLoading(false);
    }
  };

  const onCreateRequest = async () => {
    setRequestSubmitting(true);
    try {
      const type = (requestType || '').trim();
      const title = (requestTitle || '').trim();
      const description = (requestDescription || '').trim();

      if (!type) {
        message.error('نوع درخواست الزامی است');
        return;
      }
      if (!title) {
        message.error('عنوان درخواست الزامی است');
        return;
      }
      if (title.length > 255) {
        message.error('عنوان حداکثر ۲۵۵ کاراکتر است');
        return;
      }
      if (description.length > 2000) {
        message.error('توضیحات حداکثر ۲۰۰۰ کاراکتر است');
        return;
      }

      const payload = { request_type: type, title };
      if (description) payload.description = description;
      if (requestActionItemId) payload.action_item_id = Number(requestActionItemId);
      
      // استفاده از action ID که قبلاً sync شده است
      if (requestedToActionId) {
        payload.requested_to = requestedToActionId;
      }

      const metadata = {};
      if (type === 'تمدید_ددلاین') {
        if (!requestDeadline) {
          message.error('تاریخ ددلاین جدید برای تمدید ددلاین الزامی است');
          return;
        }
        // تبدیل تاریخ با ساعت به فرمت میلادی برای API: "2025-12-28 14:00:00"
        const apiDate = formatDate(requestDeadline);
        if (!apiDate) {
          message.error('تاریخ ددلاین جدید نامعتبر است');
          return;
        }
        // بررسی اینکه تاریخ بعد از امروز باشد
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const deadlineDate = new Date(apiDate);
        if (deadlineDate <= today) {
          message.error('تاریخ ددلاین جدید باید بعد از امروز باشد');
          return;
        }
        metadata.new_deadline = apiDate;
      }
      if (type === 'انتقال_مسئولیت') {
        if (!newUserActionId) {
          message.error('انتخاب کاربر جدید برای انتقال مسئولیت الزامی است');
          return;
        }
        metadata.new_user_id = newUserActionId;
      }
      if (Object.keys(metadata).length > 0) {
        payload.metadata = metadata;
      }

      const res = await actionApi.createActionRequest(id, payload);
      if (res?.success) {
        message.success(getSuccessMessage(res.message, 'درخواست با موفقیت ثبت شد'));
        setRequestType(undefined);
        setRequestTitle('');
        setRequestDescription('');
        setRequestRequestedTo('');
        setRequestActionItemId('');
        setRequestDeadline(null);
        setRequestNewUserId('');
        setRequestedToActionId(null);
        setNewUserActionId(null);
        await loadRequests();
      } else {
        message.error(res?.message || 'خطا در ثبت درخواست');
      }
    } catch (error) {
      console.error(error);
      if (error instanceof ActionValidationError) {
        const msg =
          error.errors?.request_type?.[0] ||
          error.errors?.title?.[0] ||
          error.errors?.description?.[0] ||
          error.errors?.metadata?.[0] ||
          error.message;
        message.error(msg || 'خطا در اعتبارسنجی درخواست');
      } else {
        message.error(error.message || 'خطا در ثبت درخواست');
      }
    } finally {
      setRequestSubmitting(false);
    }
  };

  // تابع برای نمایش نقش‌های کاربر
  const getRoleLabel = (role) => {
    const roleLabels = {
      'مسئول_اجرا': 'مسئول اجرا',
      'پاسخگو': 'پاسخگو',
      'مشاور': 'مشاور',
      'مطلع': 'مطلع',
    };
    return roleLabels[role] || role;
  };

  // تابع برای نمایش اولویت با رنگ
  const getPriorityConfig = (priority) => {
    const configs = {
      'فوری_و_اضطراری': { label: 'فوری و اضطراری', color: 'red' },
      'بسیار_مهم': { label: 'بسیار مهم', color: 'volcano' },
      'مهم': { label: 'مهم', color: 'orange' },
      'کم_اهمیت': { label: 'کم اهمیت', color: 'green' },
      'غیرضروری': { label: 'غیرضروری', color: 'default' },
    };
    return configs[priority] || { label: priority, color: 'default' };
  };

  // تابع برای نمایش وضعیت با رنگ
  const getStatusConfig = (status) => {
    const configs = {
      'باز': { label: 'باز', color: 'blue' },
      'در_حال_برنامه_ریزی': { label: 'در حال برنامه‌ریزی', color: 'orange' },
      'در_حال_انجام': { label: 'در حال انجام', color: 'cyan' },
      'بسته': { label: 'بسته', color: 'default' },
    };
    return configs[status] || { label: status, color: 'default' };
  };

  const header = (
    <Card bordered={false} style={{ borderRadius: 18 }} bodyStyle={{ padding: '16px 12px' }} className="mb-4">
      {/* ردیف اول: دکمه بازگشت و عنوان اقدام */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4">
        <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
          <Button
            type="text"
            icon={<ArrowRightOutlined />}
            onClick={() => navigate('/action/my-actions')}
            style={{ width: 36, height: 36, borderRadius: 12, flexShrink: 0 }}
            className="!hidden sm:!flex"
          />
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-amber-400 via-yellow-400 to-amber-500 flex items-center justify-center shadow-lg shadow-amber-500/30 flex-shrink-0">
            <ThunderboltOutlined className="text-white text-lg sm:text-xl" />
          </div>
          <div className="flex-1 min-w-0">
            {actionData?.title ? (
              <Title 
                level={2} 
                style={{ 
                  margin: 0, 
                  fontFamily: 'CustomFont', 
                  fontWeight: 700,
                  fontSize: 'clamp(18px, 4vw, 24px)',
                  lineHeight: '1.3',
                  color: '#1f2937'
                }}
                className="dark:text-white break-words"
              >
                {actionData.title}
              </Title>
            ) : (
              <Title level={2} style={{ margin: 0, fontFamily: 'CustomFont', fontWeight: 700, fontSize: 'clamp(18px, 4vw, 24px)' }}>
                در حال بارگذاری...
              </Title>
            )}
            {/* {actionData?.number && (
              <Text type="secondary" style={{ fontFamily: 'CustomFont', fontSize: '13px', marginTop: 4, display: 'block' }}>
                شناسه: {actionData.number}
              </Text>
            )} */}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 flex-shrink-0 w-full lg:w-auto">
          {/* دکمه‌های اقدام بر اساس نقش‌های کاربر در این اقدام */}
          {myRoles.includes('مسئول_اجرا') && !isAccepted && (
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={onAccept}
              loading={acceptLoading}
              size="small"
              className="!text-xs sm:!text-sm"
              style={{
                borderRadius: 999,
                fontFamily: 'CustomFont',
                background: '#2563eb',
                borderColor: '#2563eb',
                paddingInline: '12px 16px',
                fontSize: 'clamp(11px, 2vw, 13px)',
                boxShadow: '0 8px 18px rgba(37, 99, 235, 0.35)',
                height: 'auto',
                whiteSpace: 'nowrap',
              }}
            >
              <span className="hidden sm:inline">پذیرش اقدام</span>
              <span className="sm:hidden">پذیرش</span>
            </Button>
          )}
          {myRoles.includes('مسئول_اجرا') && !hasCloseRequest && (
            <Button
              type="default"
              onClick={handleRequestCloseClick}
              loading={requestCloseLoading}
              size="small"
              className="!text-xs sm:!text-sm"
              style={{
                borderRadius: 999,
                fontFamily: 'CustomFont',
                background: 'rgba(249, 115, 22, 0.06)',
                borderColor: 'rgba(251, 146, 60, 0.8)',
                color: '#ea580c',
                paddingInline: '10px 14px',
                fontSize: 'clamp(11px, 2vw, 13px)',
                height: 'auto',
                whiteSpace: 'nowrap',
              }}
            >
              <span className="hidden sm:inline">ارسال درخواست بستن</span>
              <span className="sm:hidden">درخواست بستن</span>
            </Button>
          )}
          {myRoles.includes('پاسخگو') && actionData?.status !== 'بسته' && (
            <Button
              type="primary"
              icon={<StopOutlined />}
              onClick={onClose}
              loading={closeLoading}
              size="small"
              className="!text-xs sm:!text-sm"
              style={{
                borderRadius: 999,
                fontFamily: 'CustomFont',
                background: 'linear-gradient(135deg, #ef4444, #b91c1c)',
                border: 'none',
                paddingInline: '12px 16px',
                fontSize: 'clamp(11px, 2vw, 13px)',
                boxShadow: '0 8px 20px rgba(239, 68, 68, 0.35)',
                height: 'auto',
                whiteSpace: 'nowrap',
              }}
            >
              <span className="hidden sm:inline">بستن اقدام</span>
              <span className="sm:hidden">بستن</span>
            </Button>
          )}

          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={() => form.submit()}
            loading={loading}
            size="small"
            className="!text-xs sm:!text-sm"
            style={{ 
              borderRadius: 14, 
              fontFamily: 'CustomFont', 
              background: '#10b981', 
              borderColor: '#10b981',
              paddingInline: '12px 16px',
              fontSize: 'clamp(11px, 2vw, 13px)',
              height: 'auto',
              whiteSpace: 'nowrap',
            }}
          >
            <span className="hidden sm:inline">ذخیره تغییرات</span>
            <span className="sm:hidden">ذخیره</span>
          </Button>
          {canDelete && (
          <Popconfirm
            title="حذف اقدام"
            description="آیا از حذف این اقدام مطمئن هستید؟"
            okText="حذف"
            cancelText="انصراف"
            onConfirm={onDelete}
            okButtonProps={{ danger: true }}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
              style={{
                borderRadius: 999,
                fontFamily: 'CustomFont',
                paddingInline: 12,
                height: 'auto',
              }}
            />
          </Popconfirm>
          )}
        </div>
      </div>

      {/* ردیف دوم: اطلاعات اقدام */}
      {actionData && (
        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {/* اولویت */}
            {actionData.priority && (
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
                  <RiseOutlined className="text-blue-500 text-sm" />
                </div>
                <div className="flex-1 min-w-0">
                  <Text type="secondary" style={{ fontFamily: 'CustomFont', fontSize: 11, display: 'block' }}>
                    اولویت
                  </Text>
                  <Tag 
                    color={getPriorityConfig(actionData.priority).color} 
                    style={{ fontFamily: 'CustomFont', fontSize: 12, marginTop: 2 }}
                  >
                    {getPriorityConfig(actionData.priority).label}
                  </Tag>
                </div>
              </div>
            )}

            {/* نوع اقدام */}
            {actionData.action_type && (
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center flex-shrink-0">
                  <FileTextOutlined className="text-purple-500 text-sm" />
                </div>
                <div className="flex-1 min-w-0">
                  <Text type="secondary" style={{ fontFamily: 'CustomFont', fontSize: 11, display: 'block' }}>
                    نوع اقدام
                  </Text>
                  <Text style={{ fontFamily: 'CustomFont', fontSize: 13, fontWeight: 500, marginTop: 2, display: 'block' }}>
                    {actionData.action_type}
                  </Text>
                </div>
              </div>
            )}

            {/* وضعیت */}
            {actionData.status && (
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-cyan-50 dark:bg-cyan-900/20 flex items-center justify-center flex-shrink-0">
                  <CheckCircleOutlined className="text-cyan-500 text-sm" />
                </div>
                <div className="flex-1 min-w-0">
                  <Text type="secondary" style={{ fontFamily: 'CustomFont', fontSize: 11, display: 'block' }}>
                    وضعیت
                  </Text>
                  <Tag 
                    color={getStatusConfig(actionData.status).color} 
                    style={{ fontFamily: 'CustomFont', fontSize: 12, marginTop: 2 }}
                  >
                    {getStatusConfig(actionData.status).label}
                  </Tag>
                </div>
              </div>
            )}

            {/* نقش کاربری */}
            {myRoles && myRoles.length > 0 && (
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center flex-shrink-0">
                  <UserOutlined className="text-emerald-500 text-sm" />
                </div>
                <div className="flex-1 min-w-0">
                  <Text type="secondary" style={{ fontFamily: 'CustomFont', fontSize: 11, display: 'block' }}>
                    نقش شما
                  </Text>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {myRoles.map((role, idx) => (
                      <Tag key={idx} color="blue" style={{ fontFamily: 'CustomFont', fontSize: 11 }}>
                        {getRoleLabel(role)}
                      </Tag>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ردیف سوم: تاریخ‌ها و وضعیت‌ها */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-4">
            {/* تاریخ شروع برنامه‌ریزی */}
            {actionData.planned_start_date && (
              <div className="flex items-center gap-2">
                <CalendarOutlined className="text-blue-400 text-sm flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <Text type="secondary" style={{ fontFamily: 'CustomFont', fontSize: 11, display: 'block' }}>
                    شروع برنامه‌ریزی
                  </Text>
                  <Text style={{ fontFamily: 'CustomFont', fontSize: 12, marginTop: 2, display: 'block' }}>
                    {actionData.planned_start_date}
                  </Text>
                </div>
              </div>
            )}

            {/* تاریخ پایان برنامه‌ریزی */}
            {actionData.planned_end_date && (
              <div className="flex items-center gap-2">
                <CalendarOutlined className="text-blue-400 text-sm flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <Text type="secondary" style={{ fontFamily: 'CustomFont', fontSize: 11, display: 'block' }}>
                    پایان برنامه‌ریزی
                  </Text>
                  <Text style={{ fontFamily: 'CustomFont', fontSize: 12, marginTop: 2, display: 'block' }}>
                    {actionData.planned_end_date}
                  </Text>
                </div>
              </div>
            )}

            {/* تاریخ ایجاد */}
            {actionData.created_at && (
              <div className="flex items-center gap-2">
                <ClockCircleOutlined className="text-gray-400 text-sm flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <Text type="secondary" style={{ fontFamily: 'CustomFont', fontSize: 11, display: 'block' }}>
                    تاریخ ایجاد
                  </Text>
                  <Text style={{ fontFamily: 'CustomFont', fontSize: 12, marginTop: 2, display: 'block' }}>
                    {actionData.created_at}
                  </Text>
                </div>
              </div>
            )}

            {/* وضعیت پذیرش و درخواست بستن */}
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gray-50 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                <CheckCircleOutlined className="text-gray-400 text-sm" />
              </div>
              <div className="flex-1 min-w-0">
                <Text type="secondary" style={{ fontFamily: 'CustomFont', fontSize: 11, display: 'block' }}>
                  وضعیت‌ها
                </Text>
                <div className="flex flex-wrap gap-1 mt-1">
                  {isAccepted ? (
                    <Tag color="success" style={{ fontFamily: 'CustomFont', fontSize: 11 }}>
                      پذیرفته شده
                    </Tag>
                  ) : (
                    <Tag color="warning" style={{ fontFamily: 'CustomFont', fontSize: 11 }}>
                      پذیرفته نشده
                    </Tag>
                  )}
                  {hasCloseRequest && (
                    <Tag color="orange" style={{ fontFamily: 'CustomFont', fontSize: 11 }}>
                      درخواست بستن
                    </Tag>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ردیف چهارم: اعضای RACI */}
          {actionData?.racis && actionData.racis.length > 0 && (
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700 mt-4">
              <div className="flex items-center gap-2 mb-3">
                <TeamOutlined className="text-[14px] text-blue-500" />
                <Text
                  type="secondary"
                  style={{ fontFamily: 'CustomFont', fontSize: 12, fontWeight: 500 }}
                >
                  تیم RACI:
                </Text>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {actionData.racis.map((raci) => {
                  const roleColors = {
                    'پاسخگو': { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-600 dark:text-blue-400', border: 'border-blue-200 dark:border-blue-800' },
                    'مسئول_اجرا': { bg: 'bg-emerald-50 dark:bg-emerald-900/20', text: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-200 dark:border-emerald-800' },
                    'مطلع': { bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-purple-600 dark:text-purple-400', border: 'border-purple-200 dark:border-purple-800' },
                    'مشاور': { bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-600 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-800' },
                  };
                  const roleColor = roleColors[raci.raci_role] || { bg: 'bg-gray-50 dark:bg-gray-800', text: 'text-gray-600 dark:text-gray-400', border: 'border-gray-200 dark:border-gray-700' };
                  
                  return (
                    <Tooltip
                      key={raci.id}
                      title={
                        <div style={{ fontFamily: 'CustomFont' }}>
                          <div style={{ fontWeight: 600, marginBottom: 4 }}>{raci.user?.name || 'نامشخص'}</div>
                          <div style={{ fontSize: 11 }}>نقش: {raci.raci_role}</div>
                          {raci.user?.email && (
                            <div style={{ fontSize: 11, opacity: 0.8 }}>{raci.user.email}</div>
                          )}
                        </div>
                      }
                    >
                      <div
                        className={`flex items-center gap-1.5 px-2.5 py-2 rounded-lg border ${roleColor.bg} ${roleColor.border} ${roleColor.text} transition-all hover:shadow-sm cursor-default`}
                      >
                        <Avatar
                          size={24}
                          src={raci.user?.profile_img}
                          style={{ flexShrink: 0 }}
                        >
                          {raci.user?.name ? String(raci.user.name).charAt(0) : '?'}
                        </Avatar>
                        <Text
                          style={{
                            fontFamily: 'CustomFont',
                            fontSize: 12,
                            fontWeight: 500,
                            maxWidth: 120,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {raci.user?.name || 'نامشخص'}
                        </Text>
                        <Tag
                          size="small"
                          style={{
                            fontFamily: 'CustomFont',
                            fontSize: 10,
                            margin: 0,
                            padding: '0 8px',
                            height: 20,
                            lineHeight: '20px',
                            borderRadius: 4,
                          }}
                          className={roleColor.bg}
                        >
                          {raci.raci_role}
                        </Tag>
                      </div>
                    </Tooltip>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );

  const overviewTab = (
    <Row gutter={[12, 12]}>
      <Col xs={24} md={12}>
        <Form.Item
          name="title"
          label="عنوان"
          rules={[{ max: 255, message: 'حداکثر ۲۵۵ کاراکتر مجاز است' }]}
        >
          <Input size="large" style={{ borderRadius: 14 }} />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item name="objective" label="هدف">
          <Input size="large" style={{ borderRadius: 14 }} />
        </Form.Item>
      </Col>
      <Col xs={24}>
        <Form.Item name="description" label="توضیحات">
          <TextArea rows={3} style={{ borderRadius: 14 }} />
        </Form.Item>
      </Col>

      <Col xs={24} md={8}>
        <Form.Item name="priority" label="اولویت">
          <Select
            size="large"
            allowClear
            placeholder="انتخاب"
            style={{ borderRadius: 14 }}
            options={enumOptions.priorities.map((p) => ({
              value: p.value,
              label: (
                <Space>
                  <Tag color={p.color}>{p.label}</Tag>
                </Space>
              ),
            }))}
          />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item name="action_type" label="نوع اقدام">
          <Select
            size="large"
            allowClear
            placeholder="انتخاب"
            style={{ borderRadius: 14 }}
            options={enumOptions.actionTypes.map((t) => ({ value: t.value, label: t.label }))}
          />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item name="status" label="وضعیت">
          <Select
            size="large"
            allowClear
            placeholder="انتخاب"
            style={{ borderRadius: 14 }}
            options={enumOptions.statuses.map((s) => ({
              value: s.value,
              label: (
                <Space>
                  <Tag color={s.color || 'default'}>{s.label}</Tag>
                </Space>
              ),
            }))}
          />
        </Form.Item>
      </Col>

      <Col xs={24} md={12}>
        <Form.Item name="planned_start_date" label="تاریخ شروع برنامه‌ریزی‌شده">
          <PersianDatePicker
            placeholder="انتخاب تاریخ و ساعت"
            showTime={true}
            style={{ width: '100%', height: '40px', borderRadius: 12 }}
          />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item
          name="planned_end_date"
          label="تاریخ پایان برنامه‌ریزی‌شده"
          dependencies={['planned_start_date']}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                const start = getFieldValue('planned_start_date');
                if (!start || !value) return Promise.resolve();
                
                // تبدیل DateObject به moment برای مقایسه
                let startMoment, endMoment;
                
                if (start?.toDate && typeof start.toDate === 'function') {
                  startMoment = moment(start.toDate());
                } else if (typeof start === 'string') {
                  // اگر رشته است، parse می‌کنیم
                  const startDatePart = start.split(' ')[0].split(' | ')[0];
                  startMoment = moment(startDatePart, 'jYYYY/jMM/jDD');
                } else {
                  startMoment = moment(start);
                }
                
                if (value?.toDate && typeof value.toDate === 'function') {
                  endMoment = moment(value.toDate());
                } else if (typeof value === 'string') {
                  // اگر رشته است، parse می‌کنیم
                  const endDatePart = value.split(' ')[0].split(' | ')[0];
                  endMoment = moment(endDatePart, 'jYYYY/jMM/jDD');
                } else {
                  endMoment = moment(value);
                }
                
                if (!startMoment || !startMoment.isValid() || !endMoment || !endMoment.isValid()) {
                  return Promise.resolve();
                }
                
                const ok = endMoment.isAfter(startMoment) || endMoment.isSame(startMoment, 'day');
                return ok ? Promise.resolve() : Promise.reject(new Error('تاریخ پایان باید بعد از تاریخ شروع باشد'));
              },
            }),
          ]}
        >
          <PersianDatePicker
            placeholder="انتخاب تاریخ و ساعت"
            showTime={true}
            style={{ width: '100%', height: '40px', borderRadius: 12 }}
          />
        </Form.Item>
      </Col>

      <Col xs={24} md={12}>
        <Form.Item
          name="source_module"
          label="ماژول منبع"
          rules={[{ max: 255, message: 'حداکثر ۲۵۵ کاراکتر مجاز است' }]}
        >
          <Select
            size="large"
            placeholder="ماژول منبع را انتخاب کنید"
            style={{ borderRadius: 14 }}
            allowClear
            options={[
              { value: 'crm', label: 'CRM' },
              { value: 'erp', label: 'ERP' },
              { value: 'hr', label: 'HR' },
              { value: 'inventory', label: 'Inventory' },
              { value: 'finance', label: 'Finance' },
              { value: 'project', label: 'Project' },
            ]}
          />
        </Form.Item>
      </Col>

      {/* دکمه ذخیره تغییرات در پایین تب */}
      <Col xs={24}>
        <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700 mt-4">
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={() => form.submit()}
            loading={loading}
            size="large"
            style={{ 
              borderRadius: 14, 
              fontFamily: 'CustomFont', 
              background: '#10b981', 
              borderColor: '#10b981',
              paddingInline: '20px 24px',
              height: 'auto',
            }}
          >
            ذخیره تغییرات
          </Button>
        </div>
      </Col>
      {/* فیلد شناسه منبع موقتاً غیرفعال شده است */}
      {/* <Col xs={24} md={8}>
        <Form.Item name="source_id" label="شناسه منبع">
          <Input type="number" size="large" style={{ borderRadius: 14 }} />
        </Form.Item>
      </Col> */}
    </Row>
  );

  return (
    <MainLayout>
      <div className="px-2 sm:px-4 md:px-6 py-3 sm:py-5 w-full max-w-full overflow-x-hidden">
        {header}

        <Card style={{ borderRadius: 18 }} bodyStyle={{ padding: '12px 8px' }} className="sm:!p-4">
          {initialLoading ? (
            <DetailPageSkeleton />
          ) : (
            <Form
              form={form}
              layout="vertical"
              onFinish={onSubmit}
              requiredMark={false}
              style={{ fontFamily: 'CustomFont' }}
            >
              <Tabs
                activeKey={activeTab}
                onChange={(key) => {
                  setActiveTab(key);
                  // فراخوانی API مربوط به تب هنگام کلیک
                  if (key === 'timeline') {
                    loadTimeline();
                  } else if (key === 'requests') {
                    loadRequests();
                  } else if (key === 'attachments') {
                    loadAttachments();
                  }
                }}
                size="middle"
                type="card"
                tabBarGutter={4}
                tabPosition="top"
                moreIcon={<FilterOutlined />}
                tabBarStyle={{
                  marginBottom: 0,
                  fontFamily: 'CustomFont',
                  fontSize: 'clamp(11px, 2vw, 13px)',
                  overflowX: 'auto',
                  overflowY: 'hidden',
                }}
                style={{
                  fontFamily: 'CustomFont',
                }}
                className="responsive-tabs"
                items={[
                  {
                    key: 'overview',
                    label: (
                      <span style={{ fontFamily: 'CustomFont', fontSize: 'clamp(11px, 2vw, 13px)' }} className="flex items-center gap-1">
                        <ThunderboltOutlined style={{ fontSize: 'clamp(12px, 2vw, 14px)' }} /> 
                        <span className="hidden sm:inline">اطلاعات کلی</span>
                        <span className="sm:hidden">اطلاعات</span>
                      </span>
                    ),
                    children: overviewTab,
                  },
                  {
                    key: 'team',
                    label: (
                      <span style={{ fontFamily: 'CustomFont', fontSize: 'clamp(11px, 2vw, 13px)' }} className="flex items-center gap-1">
                        <CheckCircleOutlined style={{ fontSize: 'clamp(12px, 2vw, 14px)' }} /> 
                        <span className="hidden sm:inline">تیم و نقش‌ها</span>
                        <span className="sm:hidden">تیم</span>
                        {racisCount > 0 && (
                          <Badge count={racisCount} size="small" style={{ marginRight: 4, marginLeft: 2 }} />
                        )}
                      </span>
                    ),
                    children: (
                      <ActionRaciTab 
                        actionId={id} 
                        primaryButtonClass={tabPrimaryBtnClass}
                        isActive={activeTab === 'team'}
                      />
                    ),
                  },
                  // تب پیشرفت موقتاً غیرفعال شده است
                  // {
                  //   key: 'progress',
                  //   label: (
                  //     <span style={{ fontFamily: 'CustomFont', fontSize: '13px' }}>
                  //       <RiseOutlined style={{ fontSize: '14px', marginLeft: '4px' }} /> پیشرفت
                  //     </span>
                  //   ),
                  //   children: (
                  //     <ActionProgressTab
                  //       progressPercent={progressPercent}
                  //       setProgressPercent={setProgressPercent}
                  //       progressComment={progressComment}
                  //       setProgressComment={setProgressComment}
                  //       progressLoading={progressLoading}
                  //       onUpdateProgress={onUpdateProgress}
                  //       primaryButtonClass={tabPrimaryBtnClass}
                  //     />
                  //   ),
                  // },
                  // {
                  //   key: 'acceptance',
                  //   // تب «پذیرش» موقتاً غیرفعال شده است؛ برگه آن در تب‌ها نمایش داده نمی‌شود.
                  //   label: null,
                  //   children: (
                  //     <Card style={{ borderRadius: 16 }} bodyStyle={{ padding: 16 }}>
                  //       <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                  //         <div>
                  //           <Title level={5} style={{ margin: 0, fontFamily: 'CustomFont' }}>
                  //             پذیرش اقدام
                  //           </Title>
                  //           <Text type="secondary" style={{ fontFamily: 'CustomFont' }}>
                  //             فقط کاربر با نقش <Text strong>مسئول اجرا</Text> می‌تواند اقدام را بپذیرد.
                  //           </Text>
                  //         </div>
                  //         <Button
                  //           type="primary"
                  //           icon={<CheckCircleOutlined />}
                  //           onClick={onAccept}
                  //           loading={acceptLoading}
                  //           className={tabPrimaryBtnClass}
                  //           style={{ borderRadius: 14, fontFamily: 'CustomFont' }}
                  //         >
                  //           پذیرش اقدام
                  //         </Button>
                  //       </div>

                  //       <div className="mt-4">
                  //         <Text style={{ fontFamily: 'CustomFont' }} strong>
                  //           توضیحات (اختیاری)
                  //         </Text>
                  //         <TextArea
                  //           value={acceptComment}
                  //           onChange={(e) => setAcceptComment(e.target.value)}
                  //           rows={4}
                  //           maxLength={1000}
                  //           showCount
                  //           placeholder="مثلاً: اقدام را پذیرفتم و شروع به کار می‌کنم"
                  //           style={{ marginTop: 8, borderRadius: 14 }}
                  //         />
                  //       </div>

                  //       <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-800">
                  //         <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                  //           <div>
                  //             <Title level={5} style={{ margin: 0, fontFamily: 'CustomFont' }}>
                  //               درخواست بستن اقدام
                  //             </Title>
                  //             <Text type="secondary" style={{ fontFamily: 'CustomFont' }}>
                  //               فقط <Text strong>مسئول اجرا</Text> می‌تواند درخواست بستن را برای <Text strong>پاسخگو</Text> ارسال کند.
                  //             </Text>
                  //           </div>
                  //           <Button
                  //             type="primary"
                  //             onClick={onRequestClose}
                  //             loading={requestCloseLoading}
                  //             className={tabPrimaryBtnClass}
                  //             style={{ borderRadius: 14, fontFamily: 'CustomFont' }}
                  //           >
                  //             ارسال درخواست بستن
                  //           </Button>
                  //         </div>

                  //         <div className="mt-4">
                  //           <Text style={{ fontFamily: 'CustomFont' }} strong>
                  //             توضیحات (الزامی)
                  //           </Text>
                  //           <TextArea
                  //             value={requestCloseComment}
                  //             onChange={(e) => setRequestCloseComment(e.target.value)}
                  //             rows={4}
                  //             maxLength={1000}
                  //             showCount
                  //             placeholder="مثلاً: کار به اتمام رسیده است. لطفاً اقدام را ببندید."
                  //             style={{ marginTop: 8, borderRadius: 14 }}
                  //           />
                  //         </div>
                  //       </div>

                  //       <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-800">
                  //         <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                  //           <div>
                  //             <Title level={5} style={{ margin: 0, fontFamily: 'CustomFont' }}>
                  //               بستن اقدام
                  //             </Title>
                  //             <Text type="secondary" style={{ fontFamily: 'CustomFont' }}>
                  //               فقط کاربر با نقش <Text strong>پاسخگو</Text> می‌تواند اقدام را ببندد.
                  //             </Text>
                  //           </div>
                  //           <Button
                  //             type="primary"
                  //             danger
                  //             onClick={onClose}
                  //             loading={closeLoading}
                  //             className="!bg-red-500 hover:!bg-red-600 !border-red-500 hover:!border-red-600 !text-white"
                  //             style={{ borderRadius: 14, fontFamily: 'CustomFont' }}
                  //           >
                  //             بستن اقدام
                  //           </Button>
                  //         </div>

                  //         <div className="mt-4">
                  //           <Text style={{ fontFamily: 'CustomFont' }} strong>
                  //             توضیحات (اختیاری)
                  //           </Text>
                  //           <TextArea
                  //             value={closeComment}
                  //             onChange={(e) => setCloseComment(e.target.value)}
                  //             rows={4}
                  //             maxLength={1000}
                  //             showCount
                  //             placeholder="مثلاً: اقدام با موفقیت بسته شد"
                  //             style={{ marginTop: 8, borderRadius: 14 }}
                  //           />
                  //         </div>
                  //       </div>
                  //     </Card>
                  //   ),
                  // },
                  {
                    key: 'comments',
                    label: (
                      <span style={{ fontFamily: 'CustomFont', fontSize: 'clamp(11px, 2vw, 13px)' }} className="flex items-center gap-1">
                        <MessageOutlined style={{ fontSize: 'clamp(12px, 2vw, 14px)' }} /> نظرات
                      </span>
                    ),
                    children: (
                      <ActionCommentsTab
                        actionId={id}
                        primaryButtonClass={tabPrimaryBtnClass}
                        isActive={activeTab === 'comments'}
                      />
                    ),
                  },
                  {
                    key: 'requests',
                    label: (
                      <span style={{ fontFamily: 'CustomFont', fontSize: 'clamp(11px, 2vw, 13px)' }} className="flex items-center gap-1">
                        <QuestionCircleOutlined style={{ fontSize: 'clamp(12px, 2vw, 14px)' }} /> 
                        <span className="hidden sm:inline">درخواست‌ها</span>
                        <span className="sm:hidden">درخواست</span>
                      </span>
                    ),
                    children: (
                      <ActionRequestsTab
                        actionId={id}
                        requests={requests}
                        requestsLoading={requestsLoading}
                        loadRequests={loadRequests}
                        requestType={requestType}
                        setRequestType={setRequestType}
                        requestTitle={requestTitle}
                        setRequestTitle={setRequestTitle}
                        requestDescription={requestDescription}
                        setRequestDescription={setRequestDescription}
                        requestRequestedTo={requestRequestedTo}
                        setRequestRequestedTo={setRequestRequestedTo}
                        requestActionItemId={requestActionItemId}
                        setRequestActionItemId={setRequestActionItemId}
                        requestDeadline={requestDeadline}
                        setRequestDeadline={setRequestDeadline}
                        requestNewUserId={requestNewUserId}
                        setRequestNewUserId={setRequestNewUserId}
                        requestSubmitting={requestSubmitting}
                        onCreateRequest={onCreateRequest}
                        primaryButtonClass={tabPrimaryBtnClass}
                        setRequestedToActionId={setRequestedToActionId}
                        setNewUserActionId={setNewUserActionId}
                        requestedToActionId={requestedToActionId}
                        newUserActionId={newUserActionId}
                      />
                    ),
                  },
                  {
                    key: 'items',
                    label: (
                      <span style={{ fontFamily: 'CustomFont', fontSize: 'clamp(11px, 2vw, 13px)' }} className="flex items-center gap-1">
                        <OrderedListOutlined style={{ fontSize: 'clamp(12px, 2vw, 14px)' }} /> آیتم‌ها
                        {itemsCount > 0 && (
                          <Badge count={itemsCount} size="small" style={{ marginRight: 4, marginLeft: 2 }} />
                        )}
                      </span>
                    ),
                    children: (
                      <ActionItemsTab actionId={id} primaryButtonClass={tabPrimaryBtnClass} />
                    ),
                  },
                  {
                    key: 'timeline',
                    label: (
                      <span style={{ fontFamily: 'CustomFont', fontSize: 'clamp(11px, 2vw, 13px)' }} className="flex items-center gap-1">
                        <HistoryOutlined style={{ fontSize: 'clamp(12px, 2vw, 14px)' }} /> 
                        <span className="hidden sm:inline">تایم‌لاین</span>
                        <span className="sm:hidden">زمان</span>
                      </span>
                    ),
                    children: (
                      <Card style={{ borderRadius: 16 }} bodyStyle={{ padding: 16 }}>
                        <div className="mb-6">
                          <Title level={5} style={{ margin: 0, fontFamily: 'CustomFont' }}>
                            تایم‌لاین اقدام
                          </Title>
                          {/* <Text type="secondary" style={{ fontFamily: 'CustomFont' }}>
                            تمام رویدادها و تغییرات مرتبط با این اقدام به ترتیب زمانی
                          </Text> */}
                        </div>

                        {/* فیلترها */}
                        <div
                          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden transition-all duration-300 mb-4"
                        >
                          {/* هدر پنل */}
                          <button
                            type="button"
                            onClick={() => setTimelineFiltersOpen(!timelineFiltersOpen)}
                            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                          >
                            <div className="flex items-center gap-2">
                              <FilterOutlined className="text-gray-600 dark:text-gray-400" />
                              <span
                                className="text-sm font-semibold text-gray-900 dark:text-gray-100"
                                style={{ fontFamily: 'CustomFont' }}
                              >
                                فیلترها
                              </span>
                            </div>
                            {timelineFiltersOpen ? (
                              <UpOutlined className="text-gray-400 dark:text-gray-500" />
                            ) : (
                              <DownOutlined className="text-gray-400 dark:text-gray-500" />
                            )}
                          </button>

                          {/* محتوای فیلترها */}
                          {timelineFiltersOpen && (
                            <div className="p-4 pt-0 border-t border-gray-200 dark:border-gray-700">
                              <Row gutter={[12, 12]}>
                                <Col xs={24} sm={12} md={8}>
                                  <label
                                    className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5"
                                    style={{ fontFamily: 'CustomFont' }}
                                  >
                                    نوع رویداد
                                  </label>
                                  <Select
                                    mode="multiple"
                                    placeholder="همه انواع رویداد"
                                    value={timelineFilters.event_type}
                                    onChange={(val) =>
                                      setTimelineFilters((prev) => ({ ...prev, event_type: val || [] }))
                                    }
                                    allowClear
                                    style={{ width: '100%', fontFamily: 'CustomFont' }}
                                    options={TimelineEventTypeOptions}
                                  />
                                </Col>
                                <Col xs={24} sm={12} md={8}>
                                  <label
                                    className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5"
                                    style={{ fontFamily: 'CustomFont' }}
                                  >
                                    شناسه کاربر
                                  </label>
                                  <Input
                                    type="number"
                                    placeholder="شناسه کاربر"
                                    value={timelineFilters.user_id}
                                    onChange={(e) =>
                                      setTimelineFilters((prev) => ({ ...prev, user_id: e.target.value }))
                                    }
                                    allowClear
                                    style={{ fontFamily: 'CustomFont' }}
                                  />
                                </Col>
                                <Col xs={24} sm={12} md={4}>
                                  <label
                                    className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5"
                                    style={{ fontFamily: 'CustomFont' }}
                                  >
                                    تاریخ از
                                  </label>
                                  <PersianDatePicker
                                    value={timelineFilters.date_from}
                                    onChange={(val) =>
                                      setTimelineFilters((prev) => ({ ...prev, date_from: val }))
                                    }
                                    placeholder="تاریخ از"
                                    style={{ width: '100%' }}
                                  />
                                </Col>
                                <Col xs={24} sm={12} md={4}>
                                  <label
                                    className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5"
                                    style={{ fontFamily: 'CustomFont' }}
                                  >
                                    تاریخ تا
                                  </label>
                                  <PersianDatePicker
                                    value={timelineFilters.date_to}
                                    onChange={(val) =>
                                      setTimelineFilters((prev) => ({ ...prev, date_to: val }))
                                    }
                                    placeholder="تاریخ تا"
                                    style={{ width: '100%' }}
                                  />
                                </Col>
                              </Row>
                              <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-end gap-2">
                                <Button
                                  onClick={() => {
                                    setTimelineFilters({
                                      event_type: [],
                                      user_id: '',
                                      date_from: '',
                                      date_to: '',
                                    });
                                    setTimeout(() => loadTimeline(), 0);
                                  }}
                                  style={{ fontFamily: 'CustomFont' }}
                                >
                                  ریست
                                </Button>
                                <Button
                                  type="primary"
                                  onClick={loadTimeline}
                                  disabled={timelineLoading}
                                  className={tabPrimaryBtnClass}
                                  style={{ fontFamily: 'CustomFont' }}
                                >
                                  اعمال فیلترها
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="mt-6">
                          {timelineLoading ? (
                            <CommentsSkeleton count={3} />
                          ) : timeline.length === 0 ? (
                            <Empty
                              image={Empty.PRESENTED_IMAGE_SIMPLE}
                              description={
                                <span style={{ fontFamily: 'CustomFont' }}>
                                  هنوز رویدادی ثبت نشده است
                                </span>
                              }
                            />
                          ) : (
                            <div className="relative">
                              <div
                                className="absolute top-0 bottom-0 right-4 w-0.5 bg-gray-200 dark:bg-gray-700"
                                style={{ transform: 'translateX(50%)' }}
                              />

                              <div className="space-y-4">
                                {timeline.map((event, idx) => {
                                  const eventMeta = TimelineEventTypes[event.event_type] || {
                                    label: event.event_type,
                                    color: 'default',
                                    icon: <ClockCircleOutlined />,
                                  };

                                  return (
                                    <div key={event.id || idx} className="relative flex gap-4">
                                      <div
                                        className="relative z-10 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white shadow-md"
                                        style={{
                                          backgroundColor:
                                            eventMeta.color === 'default'
                                              ? '#6b7280'
                                              : eventMeta.color === 'blue'
                                              ? '#3b82f6'
                                              : eventMeta.color === 'green'
                                              ? '#10b981'
                                              : eventMeta.color === 'red'
                                              ? '#ef4444'
                                              : eventMeta.color === 'orange'
                                              ? '#f97316'
                                              : eventMeta.color === 'gold'
                                              ? '#eab308'
                                              : eventMeta.color === 'purple'
                                              ? '#8b5cf6'
                                              : eventMeta.color === 'cyan'
                                              ? '#06b6d4'
                                              : '#6b7280',
                                        }}
                                      >
                                        {eventMeta.icon}
                                      </div>

                                      <div className="flex-1 pb-4">
                                        <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow">
                                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                                            <Tag
                                              color={eventMeta.color}
                                              icon={eventMeta.icon}
                                              style={{ fontFamily: 'CustomFont', margin: 0 }}
                                            >
                                              {eventMeta.label}
                                            </Tag>
                                            {(event.jalali_date || event.timestamp) && (
                                              <Text
                                                type="secondary"
                                                style={{ fontFamily: 'CustomFont', fontSize: 15, fontWeight: 500 }}
                                              >
                                                {event.jalali_date || event.timestamp || (event.created_at ? new Date(event.created_at).toLocaleString('fa-IR') : '')}
                                              </Text>
                                            )}
                                          </div>

                                          {/* کامنت */}
                                          {event.comment && (
                                            <div className="mb-3 p-3 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                                            <Text
                                                style={{ fontFamily: 'CustomFont', display: 'block', fontSize: 13 }}
                                              className="text-gray-700 dark:text-gray-300"
                                            >
                                                {event.comment}
                                            </Text>
                                            </div>
                                          )}

                                          {/* تغییر وضعیت */}
                                          {(event.old_status || event.new_status) && (
                                            <div className="flex items-center gap-2 mb-2">
                                              <Text style={{ fontFamily: 'CustomFont', fontSize: 12, color: '#6b7280' }}>
                                                وضعیت:
                                              </Text>
                                              {event.old_status && (
                                                <>
                                                  <Tag color="default" style={{ fontFamily: 'CustomFont', fontSize: 11 }}>
                                                    {event.old_status}
                                                  </Tag>
                                                  <span style={{ color: '#9ca3af' }}>→</span>
                                                </>
                                              )}
                                              {event.new_status && (
                                                <Tag color="blue" style={{ fontFamily: 'CustomFont', fontSize: 11 }}>
                                                  {event.new_status}
                                                </Tag>
                                              )}
                                            </div>
                                          )}

                                          {/* درصد پیشرفت */}
                                          {event.percent_complete !== null && event.percent_complete !== undefined && (
                                            <div className="flex items-center gap-2 mb-2">
                                              <Text style={{ fontFamily: 'CustomFont', fontSize: 12, color: '#6b7280' }}>
                                                پیشرفت:
                                              </Text>
                                              <Tag color="green" style={{ fontFamily: 'CustomFont', fontSize: 11 }}>
                                                {event.percent_complete}%
                                              </Tag>
                                            </div>
                                          )}

                                          {/* Metadata - RACI */}
                                          {event.metadata?.raci_role && (
                                            <div className="flex items-center gap-2 mb-2">
                                              <UserOutlined className="text-blue-500 text-xs" />
                                              <Text style={{ fontFamily: 'CustomFont', fontSize: 12, color: '#6b7280' }}>
                                                نقش:
                                              </Text>
                                              <Tag color="blue" style={{ fontFamily: 'CustomFont', fontSize: 11 }}>
                                                {event.metadata.raci_role}
                                              </Tag>
                                              {event.metadata.user_name && (
                                                <>
                                                  <Text style={{ fontFamily: 'CustomFont', fontSize: 12, color: '#6b7280' }}>
                                                    کاربر:
                                                  </Text>
                                                  <Text style={{ fontFamily: 'CustomFont', fontSize: 12, fontWeight: 500 }}>
                                                    {event.metadata.user_name}
                                                  </Text>
                                                </>
                                              )}
                                            </div>
                                          )}

                                          {/* Metadata - اقدام فرعی */}
                                          {event.metadata?.item_title && (
                                            <div className="flex items-center gap-2 mb-2">
                                              <FileTextOutlined className="text-emerald-500 text-xs" />
                                              <Text style={{ fontFamily: 'CustomFont', fontSize: 12, color: '#6b7280' }}>
                                                آیتم:
                                              </Text>
                                              {event.metadata.item_id && (
                                                <Tag color="green" style={{ fontFamily: 'CustomFont', fontSize: 11 }}>
                                                  ID: {event.metadata.item_id}
                                                </Tag>
                                              )}
                                              <Text style={{ fontFamily: 'CustomFont', fontSize: 12, fontWeight: 500 }}>
                                                {event.metadata.item_title}
                                              </Text>
                                            </div>
                                          )}

                                          {/* Metadata - تغییر پیشرفت اقدام فرعی */}
                                          {event.metadata?.old_percent !== undefined && event.metadata?.new_percent !== undefined && (
                                            <div className="flex items-center gap-2 mb-2">
                                              <RiseOutlined className="text-cyan-500 text-xs" />
                                              <Text style={{ fontFamily: 'CustomFont', fontSize: 12, color: '#6b7280' }}>
                                                پیشرفت:
                                              </Text>
                                              <Tag color="default" style={{ fontFamily: 'CustomFont', fontSize: 11 }}>
                                                {event.metadata.old_percent}%
                                              </Tag>
                                              <span style={{ color: '#9ca3af' }}>→</span>
                                              <Tag color="cyan" style={{ fontFamily: 'CustomFont', fontSize: 11 }}>
                                                {event.metadata.new_percent}%
                                              </Tag>
                                            </div>
                                          )}

                                          {/* Metadata - درخواست */}
                                          {event.metadata?.request_type && (
                                            <div className="flex items-center gap-2 mb-2">
                                              <QuestionCircleOutlined className="text-orange-500 text-xs" />
                                              <Text style={{ fontFamily: 'CustomFont', fontSize: 12, color: '#6b7280' }}>
                                                نوع درخواست:
                                              </Text>
                                              <Tag color="orange" style={{ fontFamily: 'CustomFont', fontSize: 11 }}>
                                                {event.metadata.request_type}
                                              </Tag>
                                              {event.metadata.request_id && (
                                                <>
                                                  <Text style={{ fontFamily: 'CustomFont', fontSize: 12, color: '#6b7280' }}>
                                                    شناسه:
                                                  </Text>
                                                  <Tag color="default" style={{ fontFamily: 'CustomFont', fontSize: 11 }}>
                                                    {event.metadata.request_id}
                                                  </Tag>
                                                </>
                                              )}
                                              {event.metadata.title && (
                                                <>
                                                  <Text style={{ fontFamily: 'CustomFont', fontSize: 12, color: '#6b7280' }}>
                                                    عنوان:
                                                  </Text>
                                                  <Text style={{ fontFamily: 'CustomFont', fontSize: 12, fontWeight: 500 }}>
                                                    {event.metadata.title}
                                                  </Text>
                                                </>
                                              )}
                                            </div>
                                          )}

                                          {/* شناسه آیتم اقدام */}
                                          {event.action_item_id && (
                                            <div className="flex items-center gap-2 mb-2">
                                              <FileTextOutlined className="text-emerald-500 text-xs" />
                                              <Text style={{ fontFamily: 'CustomFont', fontSize: 12, color: '#6b7280' }}>
                                                شناسه آیتم اقدام:
                                              </Text>
                                              <Tag color="green" style={{ fontFamily: 'CustomFont', fontSize: 11 }}>
                                                {event.action_item_id}
                                              </Tag>
                                            </div>
                                          )}

                                          {/* اطلاعات کاربر */}
                                          {event.user && (
                                            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex items-center gap-2">
                                              <Avatar size="small" icon={<UserOutlined />}>
                                                {String(event.user?.name || '').slice(0, 1) || '?'}
                                              </Avatar>
                                              <div className="flex-1">
                                              <Text
                                                  style={{ fontFamily: 'CustomFont', fontSize: 12, fontWeight: 500 }}
                                                  className="text-gray-700 dark:text-gray-300"
                                              >
                                                {event.user?.name || 'کاربر ناشناس'}
                                              </Text>
                                                {event.user?.email && (
                                                  <Text
                                                    type="secondary"
                                                    style={{ fontFamily: 'CustomFont', fontSize: 11, display: 'block' }}
                                                  >
                                                    {event.user.email}
                                                  </Text>
                                                )}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </Card>
                    ),
                  },
                  {
                    key: 'attachments',
                    label: (
                      <span style={{ fontFamily: 'CustomFont', fontSize: 'clamp(11px, 2vw, 13px)' }} className="flex items-center gap-1">
                        <PaperClipOutlined style={{ fontSize: 'clamp(12px, 2vw, 14px)' }} /> 
                        <span className="hidden sm:inline">پیوست‌ها</span>
                        <span className="sm:hidden">پیوست</span>
                        {attachmentsCount > 0 && (
                          <Badge count={attachmentsCount} size="small" style={{ marginRight: 4, marginLeft: 2 }} />
                        )}
                      </span>
                    ),
                    children: (
                      <ActionAttachmentsTab
                        actionId={id}
                        attachments={attachments}
                        attachmentsLoading={attachmentsLoading}
                        attachmentContext={attachmentContext}
                        setAttachmentContext={setAttachmentContext}
                        attachmentDescription={attachmentDescription}
                        setAttachmentDescription={setAttachmentDescription}
                        attachmentUploading={attachmentUploading}
                        setAttachmentUploading={setAttachmentUploading}
                        attachmentFileList={attachmentFileList}
                        setAttachmentFileList={setAttachmentFileList}
                        editingAttachmentId={editingAttachmentId}
                        setEditingAttachmentId={setEditingAttachmentId}
                        editingAttachmentDescription={editingAttachmentDescription}
                        setEditingAttachmentDescription={setEditingAttachmentDescription}
                        editingAttachmentLoading={editingAttachmentLoading}
                        loadAttachments={loadAttachments}
                        primaryButtonClass={tabPrimaryBtnClass}
                      />
                    ),
                  },
                  {
                    key: 'resources',
                    label: (
                      <span style={{ fontFamily: 'CustomFont', fontSize: 'clamp(11px, 2vw, 13px)' }} className="flex items-center gap-1">
                        <AppstoreOutlined style={{ fontSize: 'clamp(12px, 2vw, 14px)' }} /> 
                        <span className="hidden sm:inline">منابع</span>
                        <span className="sm:hidden">منابع</span>
                        {resourcesCount > 0 && (
                          <Badge count={resourcesCount} size="small" style={{ marginRight: 4, marginLeft: 2 }} />
                        )}
                      </span>
                    ),
                    children: (
                      <ActionResourcesTab
                        actionId={id}
                        primaryButtonClass={tabPrimaryBtnClass}
                      />
                    ),
                  },
                ]}
              />
            </Form>
          )}
        </Card>
      </div>

      {/* استایل سفارشی برای تب‌ها */}
      <style jsx global>{`
        .ant-tabs-card > .ant-tabs-nav {
          overflow-x: auto;
          overflow-y: hidden;
          scrollbar-width: thin;
          -ms-overflow-style: none;
        }
        .ant-tabs-card > .ant-tabs-nav::-webkit-scrollbar {
          height: 4px;
        }
        .ant-tabs-card > .ant-tabs-nav::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 2px;
        }
        .ant-tabs-card > .ant-tabs-nav .ant-tabs-tab {
          padding: 8px 12px !important;
          font-size: 13px !important;
          min-height: 36px !important;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .ant-tabs-card > .ant-tabs-nav .ant-tabs-tab-active {
          font-weight: 500;
        }
        .ant-tabs-card > .ant-tabs-nav .ant-tabs-tab .anticon {
          font-size: 14px !important;
        }
        @media (max-width: 640px) {
          .ant-tabs-card > .ant-tabs-nav .ant-tabs-tab {
            padding: 6px 8px !important;
            font-size: 11px !important;
            min-height: 32px !important;
          }
          .ant-tabs-card > .ant-tabs-nav .ant-tabs-tab .anticon {
            font-size: 12px !important;
          }
        }
        @media (max-width: 480px) {
          .ant-tabs-card > .ant-tabs-nav .ant-tabs-tab {
            padding: 5px 6px !important;
            font-size: 10px !important;
            min-height: 30px !important;
          }
        }
      `}</style>

      {/* Modal درخواست بستن */}
      <Modal
        title="درخواست بستن اقدام"
        open={requestCloseModalOpen}
        onOk={onRequestClose}
        onCancel={() => {
          setRequestCloseModalOpen(false);
          setRequestCloseComment('');
        }}
        okText="ارسال درخواست"
        cancelText="انصراف"
        confirmLoading={requestCloseLoading}
        okButtonProps={{
          className: 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 border-none',
        }}
        style={{ fontFamily: 'CustomFont' }}
      >
        <div>
          <p style={{ fontFamily: 'CustomFont', marginBottom: 12, color: '#6b7280' }}>
            توضیحات درخواست بستن (اختیاری)
          </p>
          <Input.TextArea
            value={requestCloseComment}
            onChange={(e) => setRequestCloseComment(e.target.value)}
            placeholder="مثلاً: کار به اتمام رسیده است. لطفاً اقدام را ببندید."
            rows={4}
            maxLength={1000}
            showCount
            style={{ fontFamily: 'CustomFont', borderRadius: 12 }}
          />
        </div>
      </Modal>
    </MainLayout>
  );
}
